import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bookmark,
  Filter,
  Flame,
  Heart,
  Home,
  LogOut,
  MapPin,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
  X,
  Loader2,
  Undo2,
} from 'lucide-react';
import { addDoc, collection, serverTimestamp, deleteDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '@context/AuthContext';
import { useUser, useUsers } from '@hooks/useUsers';
import { calculateCompatibilityScore, getBudgetOverlap } from '@utils/compatibility';
import { firestore } from '@config/firebase';
import { User } from '../../types/user.types';
import BottomNav from '@components/BottomNav';
import TenantNav from '@components/TenantNav';
import { getProvinces, getMetrosForProvince, Province } from '@utils/locations';

interface Filters {
  provinceCode: string;
  metro: string;
  budgetMin: number;
  budgetMax: number;
  lifestyle: string[];
  city?: string;
}

const lifestyleOptions = [
  'Remote-friendly',
  'Quiet weeknights',
  'Shared dinners weekly',
  'Organized kitchen',
  'No indoor smoking',
  'Early mornings',
  'Low-noise after 10pm',
];

const SwipeInterface: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { user: me, loading: meLoading } = useUser(currentUser?.uid);
  const { users, loading: usersLoading } = useUsers();
  const [filters, setFilters] = useState<Filters>({ provinceCode: '', metro: '', budgetMin: 0, budgetMax: 0, lifestyle: [] });
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [availableMetros, setAvailableMetros] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [savingSwipe, setSavingSwipe] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [swipeHistory, setSwipeHistory] = useState<Array<{ userId: string; action: string; swipeDocId?: string }>>([]);
  const [swipedUserIds, setSwipedUserIds] = useState<Set<string>>(new Set());
  const [swipesLoaded, setSwipesLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Load provinces on mount
  useEffect(() => {
    async function loadProvinces() {
      const data = await getProvinces();
      setProvinces(data);
    }
    loadProvinces();
  }, []);

  // Load previously-swiped user IDs so they don't reappear in the deck after a refresh
  useEffect(() => {
    if (!currentUser) {
      setSwipesLoaded(true);
      return;
    }

    let cancelled = false;

    const loadExistingSwipes = async () => {
      try {
        const swipesQuery = query(
          collection(firestore, 'swipes'),
          where('fromUserId', '==', currentUser.uid)
        );
        const snapshot = await getDocs(swipesQuery);
        if (cancelled) return;
        const existingIds = snapshot.docs.map((d) => d.data().toUserId as string);
        setSwipedUserIds((prev) => new Set([...prev, ...existingIds]));
      } catch (err) {
        console.error('Error loading existing swipes:', err);
      } finally {
        if (!cancelled) setSwipesLoaded(true);
      }
    };

    void loadExistingSwipes();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (me) {
      setFilters((prev) => ({
        ...prev,
        provinceCode: me.provinceCode || prev.provinceCode,
        metro: me.metro || prev.metro,
        budgetMin: me.budgetMin || prev.budgetMin,
        budgetMax: me.budgetMax || prev.budgetMax,
      }));

      // Load metros if province is set
      if (me.provinceCode) {
        getMetrosForProvince(me.provinceCode).then(setAvailableMetros);
      }
    }
  }, [me]);

  // Store timeout refs for cleanup
  const feedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (savingSwipe || !activeCard) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handleSwipe('pass');
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleSwipe('like');
          break;
        case 'ArrowUp':
          e.preventDefault();
          handleSwipe('save');
          break;
        case 'z':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleUndo();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [savingSwipe, activeIndex]);

  // Memoize compatibility scores to avoid recalculation
  const candidatesWithScores = useMemo(() => {
    if (!me) return [];
    
    const meSafe = { ...me, lifestyle: me.lifestyle || [], interests: me.interests || [] } as User;
    
    return users
      .filter((u) => u.id !== currentUser?.uid && u.isActive && u.profileComplete)
      .map((u) => {
        const candidateSafe = { ...u, lifestyle: u.lifestyle || [], interests: u.interests || [] } as User;
        const score = calculateCompatibilityScore(meSafe, candidateSafe);
        const budgetOverlap = getBudgetOverlap(meSafe, candidateSafe);
        return { user: candidateSafe, score, budgetOverlap };
      });
  }, [users, currentUser?.uid, me]);

  const candidates = useMemo(() => {
    const filtered = candidatesWithScores.filter((item) => {
      const u = item.user;
      // Skip already swiped users
      if (swipedUserIds.has(u.id)) return false;
      
      // Province filter
      const provinceOk = filters.provinceCode ? u.provinceCode === filters.provinceCode : true;
      
      // Metro filter
      const metroOk = filters.metro ? u.metro === filters.metro : true;
      
      const budgetOk =
        filters.budgetMin && filters.budgetMax
          ? Math.min(u.budgetMax, filters.budgetMax) - Math.max(u.budgetMin, filters.budgetMin) > 0
          : true;
      const lifestyleOk = filters.lifestyle.length
        ? filters.lifestyle.every((tag) => (u.lifestyle || []).includes(tag))
        : true;
      return provinceOk && metroOk && budgetOk && lifestyleOk;
    });

    return filtered.sort((a, b) => b.score - a.score);
  }, [candidatesWithScores, filters, swipedUserIds]);

  const activeCard = candidates[activeIndex];

  const toggleLifestyle = (value: string) => {
    setFilters((prev) => {
      const exists = prev.lifestyle.includes(value);
      const next = exists ? prev.lifestyle.filter((item) => item !== value) : [...prev.lifestyle, value];
      return { ...prev, lifestyle: next };
    });
  };

  const summaryChips = useMemo(() => {
    const chips: string[] = [];
    if (filters.provinceCode) {
      const province = provinces.find(p => p.code === filters.provinceCode);
      chips.push(province?.name || filters.provinceCode);
    }
    if (filters.metro) chips.push(filters.metro);
    if (filters.budgetMin && filters.budgetMax) {
      chips.push(`R${filters.budgetMin.toLocaleString()} - R${filters.budgetMax.toLocaleString()}`);
    }
    if (filters.lifestyle.length) chips.push(`${filters.lifestyle.length} lifestyle tags`);
    return chips;
  }, [filters, provinces]);

  const handleSwipe = async (action: 'pass' | 'save' | 'like', skipAnimation = false) => {
    if (!activeCard || !currentUser || savingSwipe) {
      return;
    }

    // Check for duplicate swipe (shouldn't happen with swipedUserIds filter, but extra safety)
    if (swipedUserIds.has(activeCard.user.id)) {
      setActiveIndex((prev) => Math.min(prev + 1, candidates.length));
      return;
    }

    const targetUserId = activeCard.user.id;
    const previousIndex = activeIndex;

    // Prevent rapid-fire swipes
    setSavingSwipe(true);

    // Animate card exit
    if (!skipAnimation) {
      const direction = action === 'like' ? 1 : action === 'pass' ? -1 : 0;
      setDragOffset({ x: direction * 1000, y: -100 });
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
      await new Promise(resolve => {
        animationTimeoutRef.current = setTimeout(resolve, 300);
      });
    }

    // Optimistic UI update - advance immediately
    setFeedback(action === 'pass' ? '✕ Passed' : action === 'save' ? '⭐ Saved' : '❤️ Vibe sent!');
    setDragOffset({ x: 0, y: 0 });
    setActiveIndex((prev) => Math.min(prev + 1, candidates.length));
    setSwipedUserIds(prev => new Set([...prev, targetUserId]));

    // Clear previous feedback timeout
    if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
    feedbackTimeoutRef.current = setTimeout(() => setFeedback(null), 1500);

    try {
      // Save to Firebase
      const swipeDoc = await addDoc(collection(firestore, 'swipes'), {
        fromUserId: currentUser.uid,
        toUserId: targetUserId,
        action,
        compatibility: activeCard.score,
        createdAt: serverTimestamp(),
      });

      // Add to history for undo with doc ID
      setSwipeHistory(prev => [...prev, { userId: targetUserId, action, swipeDocId: swipeDoc.id }]);
    } catch (err) {
      console.error('Error saving swipe', err);
      setFeedback('⚠️ Could not save. Undoing...');
      
      // Rollback on error
      setActiveIndex(previousIndex);
      setSwipedUserIds(prev => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });
      
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => setFeedback(null), 2000);
    } finally {
      setSavingSwipe(false);
    }
  };

  const handleUndo = async () => {
    if (swipeHistory.length === 0 || activeIndex === 0 || savingSwipe) return;

    const lastSwipe = swipeHistory[swipeHistory.length - 1];
    if (!lastSwipe) return;

    setSavingSwipe(true);
    setFeedback('⏪ Undoing...');

    try {
      // Delete from Firebase if we have the doc ID
      if (lastSwipe.swipeDocId) {
        await deleteDoc(doc(firestore, 'swipes', lastSwipe.swipeDocId));
      }

      // Update local state
      setSwipeHistory(prev => prev.slice(0, -1));
      setActiveIndex(prev => Math.max(0, prev - 1));
      setSwipedUserIds(prev => {
        const next = new Set(prev);
        next.delete(lastSwipe.userId);
        return next;
      });
      
      setFeedback('⏪ Undone');
    } catch (err) {
      console.error('Error undoing swipe', err);
      setFeedback('⚠️ Could not undo');
    } finally {
      setSavingSwipe(false);
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => setFeedback(null), 1000);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    setDragOffset({ x: deltaX, y: deltaY });
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Responsive threshold based on screen width
    const threshold = Math.min(150, window.innerWidth * 0.3);
    if (Math.abs(dragOffset.x) > threshold) {
      if (dragOffset.x > 0) {
        handleSwipe('like', true);
      } else {
        handleSwipe('pass', true);
      }
    } else {
      setDragOffset({ x: 0, y: 0 });
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    const touch = e.touches[0];
    dragStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStartRef.current.x;
    const deltaY = touch.clientY - dragStartRef.current.y;
    setDragOffset({ x: deltaX, y: deltaY });
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Responsive threshold based on screen width (same as mouse)
    const threshold = Math.min(150, window.innerWidth * 0.3);
    if (Math.abs(dragOffset.x) > threshold) {
      if (dragOffset.x > 0) {
        handleSwipe('like', true);
      } else {
        handleSwipe('pass', true);
      }
    } else {
      setDragOffset({ x: 0, y: 0 });
    }
  };

  if (meLoading || usersLoading || !swipesLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4 pb-24 md:pb-10">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-300 animate-spin mx-auto mb-4" />
          <p className="text-text-secondary font-semibold">Loading your swipe deck...</p>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4 pb-24 md:pb-10">
        <div className="text-center space-y-3">
          <p className="text-lg font-semibold text-text-primary">Sign in to swipe.</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Go to login
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4 pb-24 md:pb-10">
        <div className="text-center space-y-3">
          <p className="text-lg font-semibold text-text-primary">Complete onboarding to unlock swipes.</p>
          <button className="btn-primary" onClick={() => navigate('/tenant/onboarding')}>
            Finish onboarding
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-3 md:px-4 py-4 md:py-6 pb-28">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-28 -right-24 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-10 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto space-y-4">
        <TenantNav />

        {/* Compact branded header */}
        <div className="text-center py-2 md:py-3">
          <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Swipe · Match · Move</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-text-primary mb-1">Swipe tailored to your vibe</h1>
      
        </div>

        {/* Compact header with inline filters */}
        <div className="card p-4 md:p-5">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-xl md:text-2xl font-extrabold text-text-primary">Find Your Roommate</h1>
                <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-300 text-xs font-semibold whitespace-nowrap flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              </div>
              <p className="text-xs md:text-sm text-text-secondary">
                {candidates.length - activeIndex} profiles • Swipe or use arrow keys
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="btn-outline py-2 px-3 md:px-4 rounded-full text-xs md:text-sm flex items-center gap-1.5 flex-shrink-0"
            >
              <Filter className="w-3.5 h-3.5 md:w-4 md:h-4" />
              {filtersOpen ? 'Hide' : 'Filters'}
              {summaryChips.length > 0 && !filtersOpen && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary-200 text-white rounded-full text-xs font-bold">
                  {summaryChips.length}
                </span>
              )}
            </button>
          </div>

          {/* Compact filter summary */}
          {!filtersOpen && summaryChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {summaryChips.map((chip) => (
                <span
                  key={chip}
                  className="px-2 py-1 rounded-full text-xs font-medium bg-primary-50 border border-primary-100 text-primary-300"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}

          {/* Expanded filters */}
          {filtersOpen && (
            <div className="space-y-3 mt-3 pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-3">
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-sm font-semibold text-text-primary mb-1">Province</label>
                  <select
                    className="input"
                    value={filters.provinceCode}
                    onChange={async (e) => {
                      const provinceCode = e.target.value;
                      setFilters((prev) => ({ ...prev, provinceCode, metro: '' }));
                      if (provinceCode) {
                        const metros = await getMetrosForProvince(provinceCode);
                        setAvailableMetros(metros);
                      } else {
                        setAvailableMetros([]);
                      }
                    }}
                  >
                    <option value="">All provinces</option>
                    {provinces.map((province) => (
                      <option key={province.code} value={province.code}>
                        {province.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-sm font-semibold text-text-primary mb-1">Metro/City</label>
                  <select
                    className="input"
                    value={filters.metro}
                    onChange={(e) => setFilters((prev) => ({ ...prev, metro: e.target.value }))}
                    disabled={!filters.provinceCode}
                  >
                    <option value="">All metros</option>
                    {availableMetros.map((metro) => (
                      <option key={metro} value={metro}>
                        {metro}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2 flex-1 min-w-[200px]">
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-1">Budget min (R)</label>
                    <input
                      type="number"
                      className="input"
                      value={filters.budgetMin}
                      onChange={(e) => setFilters((prev) => ({ ...prev, budgetMin: Number(e.target.value) }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-1">Budget max (R)</label>
                    <input
                      type="number"
                      className="input"
                      value={filters.budgetMax}
                      onChange={(e) => setFilters((prev) => ({ ...prev, budgetMax: Number(e.target.value) }))}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {lifestyleOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleLifestyle(item)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border-2 transition-all duration-200 ${
                      filters.lifestyle.includes(item)
                        ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                        : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm text-text-secondary">
                <Sparkles className="w-4 h-4 text-primary-300" /> Filters apply instantly
              </div>
            </div>
          )}
        </div>

        {activeCard ? (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div 
                ref={cardRef}
                className="card p-2 overflow-hidden transition-all duration-300 cursor-grab active:cursor-grabbing select-none"
                style={{
                  transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) rotate(${dragOffset.x * 0.03}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: Math.max(0.3, 1 - Math.abs(dragOffset.x) / 500),
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Swipe indicators */}
                {isDragging && (
                  <>
                    <div 
                      className="absolute top-8 left-8 px-6 py-3 rounded-2xl bg-red-500 text-white font-bold text-2xl rotate-[-15deg] border-4 border-red-600 shadow-lg z-10"
                      style={{ opacity: Math.max(0, -dragOffset.x / 150) }}
                    >
                      NOPE
                    </div>
                    <div 
                      className="absolute top-8 right-8 px-6 py-3 rounded-2xl bg-green-500 text-white font-bold text-2xl rotate-[15deg] border-4 border-green-600 shadow-lg z-10"
                      style={{ opacity: Math.max(0, dragOffset.x / 150) }}
                    >
                      LIKE
                    </div>
                  </>
                )}
                <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary-200 to-accent-purple text-white h-64 flex items-end p-6">
                  <div className="absolute top-0 left-0 right-0 p-4 flex items-start justify-between gap-2 flex-wrap">
                    <div className="px-3 py-2 rounded-full bg-white/20 backdrop-blur-sm text-sm font-semibold whitespace-nowrap">
                      {activeCard.score}% compatible
                    </div>
                    <div className="px-3 py-2 rounded-full bg-white/20 backdrop-blur-sm text-sm font-semibold flex items-center gap-2 whitespace-nowrap">
                      <Flame className="w-4 h-4" /> Budget overlap R{Math.max(0, activeCard.budgetOverlap.max - activeCard.budgetOverlap.min).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-3xl font-extrabold flex items-center gap-3">
                      {activeCard.user.name}
                      <span className="px-3 py-1 rounded-full bg-white/20 text-sm font-semibold">{activeCard.user.age ?? ''}</span>
                    </h2>
                    <p className="text-white/90 flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4" /> {activeCard.user.city}
                    </p>
                    <p className="text-white/90 mt-3 font-semibold">{activeCard.user.occupation}</p>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="p-4 rounded-xl bg-primary-50 border-2 border-primary-100">
                      <p className="text-sm text-text-secondary">Budget / mo (share)</p>
                      <p className="text-lg font-bold text-text-primary">R{activeCard.user.budgetMin} - R{activeCard.user.budgetMax}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-accent-blue/10 border-2 border-accent-blue/30">
                      <p className="text-sm text-text-secondary">Occupation</p>
                      <p className="text-lg font-bold text-text-primary">{activeCard.user.occupation}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-accent-purple/10 border-2 border-accent-purple/30">
                      <p className="text-sm text-text-secondary">City</p>
                      <p className="text-lg font-bold text-text-primary">{activeCard.user.city}</p>
                    </div>
                  </div>

                  <p className="text-text-secondary leading-relaxed">{activeCard.user.bio}</p>

                  {activeCard.user.dailyRhythms?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {activeCard.user.dailyRhythms.map((rhythm) => (
                        <span
                          key={rhythm}
                          className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-50 border-2 border-primary-100 text-primary-300"
                        >
                          {rhythm}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap gap-2">
                    {activeCard.user.lifestyle.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-2 rounded-full text-sm font-semibold bg-white border-2 border-primary-100 text-text-primary"
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl border-2 border-gray-200 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 font-semibold text-text-primary">
                          <Home className="w-4 h-4 text-primary-300" /> Preferred home
                        </div>
                        <span className="text-xs px-3 py-1 rounded-full bg-primary-50 text-primary-300 border border-primary-100">Curated</span>
                      </div>
                      <p className="text-sm text-text-secondary">Looks for safe, bright, organized spaces.</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-text-secondary">Fast Wi-Fi</span>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-text-secondary">Desk space</span>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-text-secondary">Secure building</span>
                      </div>
                    </div>
                    <div className="p-4 rounded-xl border-2 border-gray-200 bg-white">
                      <div className="flex items-center gap-2 text-text-primary font-semibold mb-2">
                        <Users className="w-4 h-4 text-primary-300" /> Interests
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {activeCard.user.interests.map((interest) => (
                          <span
                            key={interest}
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30"
                          >
                            {interest}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-text-secondary mt-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary-300" /> Actions tune what you see next.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm text-text-secondary px-2">
                      <span className="font-semibold">{candidates.length - activeIndex} profiles remaining</span>
                      <button
                        type="button"
                        onClick={handleUndo}
                        disabled={swipeHistory.length === 0 || activeIndex === 0}
                        className="flex items-center gap-1 px-3 py-1 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        <Undo2 className="w-3 h-3" /> Undo
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 sticky bottom-0 bg-white/80 backdrop-blur-sm p-2 -m-2 rounded-xl">
                      <button
                        type="button"
                        onClick={() => handleSwipe('pass')}
                        className="btn-secondary w-full flex items-center justify-center gap-2 text-base py-4 hover:scale-105 transition-transform active:scale-95"
                        disabled={savingSwipe}
                      >
                        <X className="w-5 h-5" /> Pass
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSwipe('save')}
                        className="btn-outline w-full flex items-center justify-center gap-2 text-base py-4 hover:scale-105 transition-transform active:scale-95"
                        disabled={savingSwipe}
                      >
                        <Bookmark className="w-5 h-5" /> Save
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSwipe('like')}
                        className="btn-primary w-full flex items-center justify-center gap-2 text-base py-4 hover:scale-105 transition-transform active:scale-95 shadow-lg"
                        disabled={savingSwipe}
                      >
                        {savingSwipe ? <Loader2 className="w-5 h-5 animate-spin" /> : <Heart className="w-5 h-5" />} 
                        Like
                      </button>
                    </div>
                  </div>

                  {feedback && (
                    <div
                      className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50 animate-bounce"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-200 to-primary-300 text-white font-bold text-lg shadow-2xl border-4 border-white">
                        {feedback}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {/* Compact keyboard shortcuts - hidden on mobile */}
              <div className="hidden lg:block card p-4">
                <div className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary-300" /> Keyboard shortcuts
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-text-secondary">
                  <div className="flex items-center gap-2">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">←</kbd>
                    <span>Pass</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">→</kbd>
                    <span>Like</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono">↑</kbd>
                    <span>Save</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono text-[10px]">⌘Z</kbd>
                    <span>Undo</span>
                  </div>
                </div>
              </div>

              {/* Compact filter summary */}
              <div className="card p-4">
                <div className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-300" /> Active filters
                </div>
                <div className="space-y-2 text-xs text-text-secondary">
                  {filters.city && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-primary-50">
                      <MapPin className="w-3.5 h-3.5 text-primary-300 flex-shrink-0" />
                      <span className="truncate">{filters.city}</span>
                    </div>
                  )}
                  {(filters.budgetMin > 0 || filters.budgetMax > 0) && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-primary-50">
                      <Wallet className="w-3.5 h-3.5 text-primary-300 flex-shrink-0" />
                      <span className="truncate">R{filters.budgetMin}k - R{filters.budgetMax}k</span>
                    </div>
                  )}
                  {filters.lifestyle.length > 0 && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-primary-50">
                      <Sparkles className="w-3.5 h-3.5 text-primary-300 flex-shrink-0" />
                      <span className="truncate">{filters.lifestyle.length} lifestyle tags</span>
                    </div>
                  )}
                  {!filters.city && filters.budgetMin === 0 && filters.budgetMax === 0 && filters.lifestyle.length === 0 && (
                    <p className="text-text-tertiary italic">No filters applied</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card p-6 text-center space-y-3">
            <p className="text-lg font-semibold text-text-primary">You’re all caught up.</p>
            <p className="text-text-secondary">Adjust filters or check back soon for new matches.</p>
          </div>
        )}

        {/* Logout Button */}
        <div className="mt-8 pb-6">
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default SwipeInterface;

